/**
 * LLM-9E/9G — fail-closed runtime guard for ephemeral memory suggestions.
 * Only user-facing fields required for display and promotion are retained.
 */

import {
  normalizeActionProposal,
  normalizePlanChangePreview,
  normalizeTrainingPlanDraft,
} from "@/lib/coachingActionGuard";

const MAX_OBSERVATION_LENGTH = 512;
const MAX_CONFIRMATION_ID_LENGTH = 128;
const MAX_PROMOTION_LENGTH = 256;
const MAX_FEEDBACK_REF_LENGTH = 64;
const FEEDBACK_REF_PATTERN = /^memfb_[0-9a-f]{16}$/;

const AUTHORITY_FIELD_DENYLIST = new Set([
  "candidate",
  "candidate_ref",
  "candidate_fingerprint",
  "exposure_count",
  "dismissal_count",
  "revision",
  "athlete_id",
  "semantic_key",
  "operation_id",
  "confirmed",
  "memory_payload",
  "snapshot",
  "memory_ref",
  "memory_id",
]);

const isNonEmptyString = (value, maxLength) =>
  typeof value === "string"
  && value.trim().length > 0
  && value.trim().length <= maxLength;

const normalizeFeedbackRef = (raw) => {
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  if (
    trimmed.length === 0
    || trimmed.length > MAX_FEEDBACK_REF_LENGTH
    || !FEEDBACK_REF_PATTERN.test(trimmed)
  ) {
    return null;
  }
  return trimmed;
};

/**
 * @param {unknown} raw
 * @returns {{ observationText: string, promotionCommand: string, feedbackRef?: string } | null}
 */
export const normalizeMemorySuggestion = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  for (const field of AUTHORITY_FIELD_DENYLIST) {
    if (Object.prototype.hasOwnProperty.call(raw, field)) {
      // Authority-like fields are ignored rather than trusted.
    }
  }

  const observationText = typeof raw.observation_text === "string"
    ? raw.observation_text.trim()
    : "";
  const promotionCommand = typeof raw.promotion_command === "string"
    ? raw.promotion_command.trim()
    : "";

  if (!isNonEmptyString(observationText, MAX_OBSERVATION_LENGTH)) {
    return null;
  }
  if (!isNonEmptyString(promotionCommand, MAX_PROMOTION_LENGTH)) {
    return null;
  }

  const feedbackRef = normalizeFeedbackRef(raw.feedback_ref);
  const normalized = { observationText, promotionCommand };
  if (feedbackRef) {
    normalized.feedbackRef = feedbackRef;
  }
  return normalized;
};

const MAX_CONVERSATION_ID_LENGTH = 128;

const normalizeConversationId = (raw) => {
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_CONVERSATION_ID_LENGTH) {
    return null;
  }
  return trimmed;
};

const normalizeConversationRevision = (raw) => {
  if (typeof raw !== "number" || !Number.isFinite(raw) || !Number.isInteger(raw)) {
    return null;
  }
  return raw >= 1 ? raw : null;
};

const normalizeConfirmationId = (raw) => {
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_CONFIRMATION_ID_LENGTH) {
    return null;
  }
  return trimmed;
};

/**
 * @param {unknown} response
 * @returns {{
 *   reply: string | null,
 *   status: string | null,
 *   memorySuggestion: ReturnType<typeof normalizeMemorySuggestion>,
 *   conversationId: string | null,
 *   conversationRevision: number | null,
 *   coachingAction: ReturnType<typeof normalizeActionProposal>,
 *   confirmationRequired: boolean,
 *   confirmationId: string | null,
 *   materialActionTaken: boolean,
 *   planChangePreview: ReturnType<typeof normalizePlanChangePreview>,
 *   requiresTrainingIntake: boolean,
 *   trainingIntakeMode: string | null,
 * }}
 */
export const normalizeAskCoachResponse = (response) => {
  if (response == null || typeof response !== "object") {
    return {
      reply: null,
      status: null,
      memorySuggestion: null,
      conversationId: null,
      conversationRevision: null,
      coachingAction: normalizeActionProposal(null),
      confirmationRequired: false,
      confirmationId: null,
      materialActionTaken: false,
      planChangePreview: null,
      requiresTrainingIntake: false,
      trainingIntakeMode: null,
    };
  }

  const reply = typeof response.reply === "string" ? response.reply : null;
  const status = typeof response.status === "string" ? response.status : null;
  const memorySuggestion = normalizeMemorySuggestion(response.memory_suggestion);
  const conversationId = normalizeConversationId(response.conversation_id);
  const conversationRevision = normalizeConversationRevision(
    response.conversation_revision,
  );
  // Training Plan Setup intent surfaced by the backend short-circuit path.
  // The intake wizard opens when this flag is true; the questionnaire never
  // consumes AI-chat quota because the backend short-circuits before any
  // gateway call. ``training_intake_mode`` picks between "new" (full wizard)
  // and "review_existing" (returning-athlete "Has anything changed?" prompt).
  const requiresTrainingIntake = response.requires_training_intake === true;
  const rawMode = typeof response.training_intake_mode === "string"
    ? response.training_intake_mode
    : null;
  const trainingIntakeMode = rawMode === "new" || rawMode === "review_existing"
    ? rawMode
    : null;
  const coachingAction = normalizeActionProposal(response.action_proposal);
  const confirmationRequired = response.confirmation_required === true;
  const confirmationId = normalizeConfirmationId(response.confirmation_id);
  const materialActionTaken = response.material_action_taken === true;
  const planChangePreview = normalizePlanChangePreview(response.plan_change_preview);
  const trainingPlanDraft = normalizeTrainingPlanDraft(response.training_plan_draft);
  const availabilityStatus =
    typeof response.availability_status === "string"
      ? response.availability_status
      : null;
  const failureCategory =
    typeof response.failure_category === "string"
      ? response.failure_category
      : null;
  const degradedMode = response.degraded_mode === true;
  const retryable = response.retryable === true;
  const correlationId =
    typeof response.correlation_id === "string"
      ? response.correlation_id
      : null;

  return {
    reply,
    status,
    memorySuggestion,
    conversationId,
    conversationRevision,
    requiresTrainingIntake,
    trainingIntakeMode,
    coachingAction,
    confirmationRequired,
    confirmationId,
    materialActionTaken,
    planChangePreview,
    trainingPlanDraft,
    availabilityStatus,
    failureCategory,
    degradedMode,
    retryable,
    correlationId,
  };
};

/**
 * Strip authority-like extra fields from a suggestion object for tests/logging.
 * The UI must never forward these into session messages.
 */
export const stripSuggestionAuthorityFields = (payload) => {
  if (payload == null || typeof payload !== "object") {
    return { message: payload };
  }
  return { message: payload.promotionCommand || payload.message };
};

export {
  AUTHORITY_FIELD_DENYLIST,
  FEEDBACK_REF_PATTERN,
  MAX_FEEDBACK_REF_LENGTH,
  normalizeFeedbackRef,
};
